import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { SubmitHandler, useForm } from 'react-hook-form';
import { quickConsultGateway } from '@/di-container/remodeling-manager/quick-consult/gateway/client';
import useAuthenticated from '@/hooks/useAuthenticated';
import { useAlert } from '@/providers/AlertProvider';
import { useWebApp } from '@/providers/WebAppProvider';
import { useConfirm } from '@/providers/ConfirmProvider';
import {
  Channel,
  QuickConsult,
  QuickConsultInfoFromUntact,
  ConsultUserBasicInfo,
} from '@remodeling/remodeling-manager';
import { SimpleUserAgent } from '@remodeling/base';
import { ConsultContentsCardItem, QuickConsultInputs } from '@/types';
import {
  CHANNEL_TYPE_TEXTS,
  GTM_CSTC,
  GTM_CST_KEYWORD,
  GTM_CTA_KEY_NAME,
  GTM_CTA_REMO_EVENT_NAME,
  GTM_QUICK_CONSULT_KEYWORD,
} from '@/constants';
import { useBraze } from '@/providers/BrazeProvider';
import { format } from 'date-fns';
import { useDataLayer } from '@/providers/DataLayerProvider';
import { expertGateway } from '@/di-container/expert/detail/gateway/client';
import useSWR from 'swr';

interface UseQuickConsultProps {
  channelType?: string | string[];
  channelId?: string | string[] | undefined;
  agentEmployeeNo?: string | string[] | undefined;
  channelUrl?: string | string[] | undefined;
  untactInfo?: QuickConsultInfoFromUntact;
  handleCloseLayerPopUp?: (id: string) => void;
  returnUrl?: string | string[];
  shopCode?: string | string[] | null;
}

const useQuickConsult = ({
  channelType,
  channelId,
  agentEmployeeNo,
  channelUrl,
  handleCloseLayerPopUp,
  returnUrl,
  shopCode,
}: UseQuickConsultProps) => {
  const [consultContentsCardItem, setConsultContentsCardItem] =
    useState<ConsultContentsCardItem>();
  const [userInfo, setUserInfo] = useState<ConsultUserBasicInfo>();
  const [isActiveSubmitButton, setIsActiveSubmitButton] = useState(false);
  const [isOpenWorkableBottomSheet, setIsOpenWorkableBottomSheet] =
    useState(false);

  const isApiCall = useRef<boolean>(false);

  const { handleOpen } = useAlert();
  const router = useRouter();
  const hookForm = useForm<QuickConsultInputs>();
  const { watch } = hookForm;
  const { isAuthenticated } = useAuthenticated();
  const { getBasic, getChannel, createQuickConsult } = quickConsultGateway();
  const {
    handleOpenWebPopUp,
    isApp,
    isIOS,
    isAOS,
    handleCloseWebPopUp,
    handleAppsFlyer,
  } = useWebApp();
  const { handleOpen: handleOpenConfirm } = useConfirm();

  const { logCustomEvent } = useBraze();

  const handleBack = useCallback(() => {
    handleOpenConfirm({
      type: 'confirm',
      cancelText: '아니오',
      confirmText: '예',
      autoZIndex: false,
      zIndex: 9999,
      handleConfirm: () => {
        if (isApp()) {
          handleCloseWebPopUp({ isReloadWindow: false });
        } else if (handleCloseLayerPopUp) {
          handleCloseLayerPopUp?.('QuickConsultLayerPopUp');
        } else {
          router.back();
        }
      },
      handleCancel: () => {},
      textOptions: {
        title: '상담신청을 취소 하시겠습니까?',
      },
    });
  }, [
    handleCloseLayerPopUp,
    handleCloseWebPopUp,
    handleOpenConfirm,
    isApp,
    router,
  ]);

  const getBasicInfo = useCallback(async () => {
    await getBasic.exec().then((response) => {
      response?.data?.user && setUserInfo(response.data.user);
    });
  }, [getBasic]);

  const getChannelInfo = useCallback(async () => {
    await getChannel
      .exec({ channelType, channelId, agentEmployeeNo, channelUrl })
      .then((response) => {
        response?.data?.channel &&
          setConsultContentsCardItem(response.data.channel);
      });
  }, [agentEmployeeNo, channelId, channelType, channelUrl, getChannel]);

  const getWorkAvailableAreasfetcjer = useCallback(async () => {
    const apiResponse = await expertGateway().getExpertDetail.exec({
      agentCode: agentEmployeeNo as string,
    });

    return apiResponse.data?.agentDetail.workAvailableAreas;
  }, [agentEmployeeNo]);

  const { data: workAvailableAreas } = useSWR(
    channelType === 'OP23_01' ? 'getWorkAvailableAreas' : null,
    getWorkAvailableAreasfetcjer,
  );

  const { handlePushAndRemove } = useDataLayer();

  const handleSubmitRequest: SubmitHandler<QuickConsultInputs> = useCallback(
    async (data) => {
      if (isApiCall.current) {
        return;
      }

      isApiCall.current = true;

      const numChannelId = Number(consultContentsCardItem?.channelId);
      let simpleUserAgent: SimpleUserAgent = 'MOBILE_WEB';
      if (isAOS()) simpleUserAgent = 'AOS';
      else if (isIOS()) simpleUserAgent = 'IOS';
      const req: QuickConsult = {
        ...data,
        name: data.name.trim(),
        // shopCode 없을경우 null로 전달
        shopCode: shopCode || null,
        channel: {
          ...(consultContentsCardItem as Channel),
          channelId: numChannelId,
        },
        simpleUserAgent,
      };

      const response = await createQuickConsult.exec(req);

      if (response?.code === 200) {
        logCustomEvent({
          eventName: 'bz_complete_fastconsult',
          eventProperties: {
            bz_date: format(new Date(), 'yyyy-MM-dd'),
            bz_consultType: 'quick',
          },
        });
        handlePushAndRemove({
          data: {
            event: GTM_CTA_REMO_EVENT_NAME,
            [GTM_CTA_KEY_NAME]: GTM_CSTC,
            consult_request_id: response?.data?.id,
            [GTM_CST_KEYWORD]: GTM_QUICK_CONSULT_KEYWORD,
          },
          key: GTM_CTA_KEY_NAME,
        });
        handleOpen({
          type: 'alert',
          textOptions: {
            title: '상담신청이 완료되었습니다',
          },
          isOffCloseBG: true,
          handleConfirm: async () => {
            if (isApp()) {
              if (isAuthenticated) {
                handleOpenWebPopUp({
                  url: '/my/consult',
                  isReplaceWindow: true,
                });
                return;
              }

              handleCloseWebPopUp({ isReloadWindow: false });
              return;
            }

            isApiCall.current = false;

            if (isAuthenticated) {
              await router.push('/my/consult');
            } else {
              await router.push(
                `${process.env.NEXT_PUBLIC_STORE_MWEB_HOST}/interior`,
              );
              handleCloseLayerPopUp?.('QuickConsultLayerPopUp');
            }
          },
          gtmConfirmTagName: `${GTM_CSTC}_confirm_button`,
        });
        if (isApp()) {
          handleAppsFlyer({
            inAppEvent: 'af_complete_booking',
            af_route: CHANNEL_TYPE_TEXTS.find(
              (item) => item.type === consultContentsCardItem?.channelType,
            )?.text,
            af_content: consultContentsCardItem?.title,
            af_content_id: consultContentsCardItem?.channelId,
            af_order_id: response?.data?.id,
          });

          isApiCall.current = false;
        }
      } else {
        if (response?.code === 14199) {
          handleOpen({
            type: 'alert',
            textOptions: {
              title:
                '고객님의 연락처로 오늘 자\n상담신청 내역이 이미 존재합니다.',
              subText: '동일한 연락처로 1일 1회 상담신청이\n가능합니다.',
            },
            isOffCloseBG: true,
            handleConfirm: () => {
              isApiCall.current = false;
            },
          });
        } else {
          isApiCall.current = false;
        }
      }
    },
    [
      consultContentsCardItem,
      isAOS,
      isIOS,
      shopCode,
      createQuickConsult,
      logCustomEvent,
      handlePushAndRemove,
      handleOpen,
      isApp,
      isAuthenticated,
      handleCloseWebPopUp,
      handleOpenWebPopUp,
      router,
      handleCloseLayerPopUp,
      handleAppsFlyer,
    ],
  );

  const handleOpenBottomSheet = useCallback(() => {
    setIsOpenWorkableBottomSheet(true);
  }, []);

  const handleCloseBottomSheet = useCallback(() => {
    setIsOpenWorkableBottomSheet(false);
  }, []);

  const checkAllInputs = useCallback(() => {
    watch((value, { name }) =>
      watch('name') &&
      watch('tel') &&
      watch('site.address.zoneCode') &&
      watch('site.address.roadnameAddress') &&
      watch('site.addressDetail') &&
      watch('agreement.privacy') === 'Y' &&
      watch('agreement.provide') === 'Y'
        ? setIsActiveSubmitButton(true)
        : setIsActiveSubmitButton(false),
    );
  }, [watch]);

  useEffect(() => {
    getChannelInfo();
    isAuthenticated && getBasicInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkAllInputs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  return {
    isOpenWorkableBottomSheet,
    handleOpenBottomSheet,
    handleCloseBottomSheet,
    handleSubmitRequest,
    hookForm,
    isActiveSubmitButton,
    userInfo,
    workAvailableAreas,
    isApp,
    handleBack,
  };
};

export default useQuickConsult;
